"use client"

import * as React from "react"

import { BankEmptyState } from "@/components/assessment-structure/bank-empty-state"
import { type BlueprintGapProgress } from "@/components/assessment-structure/blueprint-panel-shared"
import { BlueprintPanel, type BlueprintTargetIntent } from "@/components/assessment-structure/blueprint-panel"
import { TableNewRowDot } from "@/components/data-views"
import {
  QuestionRow,
  QUESTION_ROW_DIVIDER_CLASSNAME,
  QUESTION_ROW_LIST_CLASSNAME,
} from "@/components/assessment-structure/question-row"
import { SectionQuestionsTotals } from "@/components/assessment-structure/section-questions-totals"
import { LeoIcon } from "@/components/ui/leo-icon"
import { labelForContentId } from "@/lib/blueprint/mapping"
import { cn } from "@/lib/utils"
import { sectionEstimatedMinutes } from "@/lib/question-expected-time"
import { questionSignalInputFromBankRow } from "@/lib/question-preview-adapters"
import { questionNeedsReview, questionReviewSignals } from "@/lib/question-signals"
import {
  buildBloomMix,
  buildDifficultyMix,
  buildDiscriminationMix,
  buildTopicMix,
  difficultyIndexCellTone,
  difficultyIndexLabel,
  mixFacetLabel,
  mixFacetScope,
  parseMixFacetScope,
  sectionDifficultyIndex,
  DIFFICULTY_INDEX_CELLS,
  type MixBand,
  type MixGlyph,
  type MixGlyphTone,
  type MixGroup,
} from "@/lib/section-mix"
import {
  DISCRIMINATION_METHODS,
  type DiscriminationMethodId,
} from "@/lib/discrimination"
import {
  BLOOM_ICON,
  BLOOM_LEVELS,
  type AssessmentBankQuestion,
  type AssessmentBlueprintGap,
  type BloomLevel,
} from "@/lib/mock/assessment-add-questions"
import { Button } from "@exxatdesignux/ui/components/button"
import { KeyMetrics, type MetricItem } from "@/components/key-metrics"
import {
  Collapsible,
  CollapsibleTrigger,
} from "@exxatdesignux/ui/components/collapsible"
import {
  DataRowList,
  OutlineTreeCollapsibleContentRail,
  OutlineTreeExpandTrigger,
  OutlineTreeLeafButton,
  OutlineTreeMenu,
  OutlineTreeMenuItem,
  OutlineTreeSub,
  outlineTreeBranchDepthStyle,
} from "@/components/data-views"
import {
  Tabs,
  TabsContent,
  TabsCountBadge,
  TabsList,
  TabsTrigger,
  TabsTriggerIcon,
  TabsTriggerLabel,
} from "@exxatdesignux/ui/components/tabs"
import { Tip } from "@exxatdesignux/ui/components/tip"

/**
 * Mix is built and wired, but off in the product for now. Kept behind a flag
 * rather than deleted: the tab is the only reader of `MixTab` and the
 * `buildMix*` helpers, and pulling it would take the whole spread with it.
 * Typed `boolean` so the branches stay type-checked rather than being narrowed
 * to dead code.
 */
export const SHOW_MIX_TAB: boolean = false

export interface SectionGapProgress {
  gap: AssessmentBlueprintGap
  scopeValue: string
  filled: number
  satisfied: boolean
}

/** One section of the paper as Mix reports on it. */
export interface MixSectionRoster {
  id: string
  title: string
  /** Everything currently in that section, including what was added this visit. */
  rows: AssessmentBankQuestion[]
  /** The section Add questions is scoped to. */
  current: boolean
}

export interface BankNavItem {
  value: string
  label: string
  icon: string
  /**
   * How many questions this scope still has left to add. Omitted by rows that
   * are a way of looking rather than a set of rows — Search has the whole bank
   * behind it, and printing that total would restate All questions.
   */
  count?: number
  /** Trailing note, e.g. gap progress `1/2`. */
  meta?: string
  /** Leo picked this scope, so it wears his star instead of a folder glyph. */
  leo?: boolean
  /**
   * A background job is still filling this scope. The row spins in place of its
   * count, so the rail keeps saying the work is out even once the banner that
   * announced it has scrolled away.
   */
  busy?: boolean
  /** Session-new rows landed here. Same marker the pool uses on those stems. */
  isNew?: boolean
  /** Sub-folders. Counts on a parent include everything underneath it. */
  children?: BankNavItem[]
}

export interface BankNavGroup {
  id: string
  label: string
  /** Hide the group heading. The page chrome already names the section. */
  hideLabel?: boolean
  items: BankNavItem[]
  /**
   * How many rows to show before the rest go behind a Show more.
   *
   * Counted from the top of `items`, so a rollup row placed first stays
   * visible. Omit it and the group shows everything, which is right for a
   * handful of named scopes and wrong for a program with ten years of finals
   * on file.
   */
  collapseAfter?: number
}

/** `aria-labelledby` takes a space-separated token list, so ids cannot contain spaces. */
function slugify(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-")
}

/**
 * One inset for the whole rail. The roster bleeds edge-to-edge against it and
 * the sticky totals sit flush to the panel edge, so all three read from here
 * rather than three hand-kept numbers that drift apart.
 */
const PANEL_BODY_PADDING = "p-3"
const PANEL_BODY_BLEED = "-mx-3"

/**
 * The blueprint states its target as a phrase that leads with the count —
 * "2 Hard MCQ". Beside a value that already reads "2 of 2" that number is on
 * the row twice, and the extra characters were enough to truncate the topic
 * mid-word at this width. Drop it and keep the part the count is about.
 */
function gapNeedLabel(topic: string, need: string) {
  return `${topic} · ${need.replace(/^\d+\s+/, "")}`
}

/**
 * Progress toward a blueprint target — the one thing in Mix that has a real
 * denominator ("2 of 2 Hard Cardiology MCQs"), so the one thing drawn as a
 * progress bar. Shares of the section are drawn by `MixBandList` instead.
 */
function MixTargetRow({
  label,
  count,
  max,
  labelId,
  satisfied,
}: {
  label: string
  count: number
  max: number
  labelId: string
  satisfied: boolean
}) {
  const percent = max > 0 ? Math.min(100, Math.round((count / max) * 100)) : 0
  return (
    <li className="flex flex-col gap-1.5">
      <div className="flex items-baseline justify-between gap-2">
        <span id={labelId} className="min-w-0 truncate text-sm text-foreground">
          {label}
        </span>
        <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
          {count} of {max}
          {satisfied ? " · met" : null}
        </span>
      </div>
      <div
        role="progressbar"
        aria-labelledby={labelId}
        aria-valuemin={0}
        aria-valuemax={max}
        aria-valuenow={count}
        className="h-1.5 overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn(
            "h-full rounded-full",
            satisfied ? "bg-brand-1" : "bg-brand-1/62"
          )}
          style={{ width: `${percent}%` }}
        />
      </div>
    </li>
  )
}

/**
 * Stair-stepped bars, tallest last — the same signal metaphor `SignalBarsCell`
 * draws difficulty with in the pool grid, so a three-bar red glyph means "Hard"
 * on both sides of the screen.
 *
 * This is a local, non-interactive copy rather than the cell itself: the cell
 * carries `tabIndex` and a tooltip, and these rows are already buttons, so
 * mounting it here would put a focus stop and a tooltip trigger inside a
 * control. Nothing is announced — the band's label sits beside it in the same
 * button, which is the adjacent-text case in §8.6.
 */
const SCALE_BAR_HEIGHTS = ["h-2", "h-3", "h-4"] as const

/**
 * Difficulty keeps the cell's own green / amber / red so a three-bar red glyph
 * means "Hard" in both places. The unpolarised scales take brand instead of the
 * cell's near-black `info`: at this size a dark glyph sits at the same weight as
 * the label beside it, and `PBI_BANDS.meterClassName` already draws strong
 * discrimination in brand elsewhere in the product.
 */
const SCALE_TONE_CLASS: Record<MixGlyphTone, string> = {
  success: "bg-emerald-500",
  warning: "bg-amber-500",
  danger: "bg-destructive",
  info: "bg-brand-1",
  neutral: "bg-muted-foreground",
}

function MixBandGlyph({ glyph, muted }: { glyph: MixGlyph; muted: boolean }) {
  // One slot width for every dimension so labels align down the whole panel,
  // whether the row leads with three bars or a tag.
  const slot = "relative flex w-4 shrink-0 justify-center"

  if (glyph.kind === "icon") {
    return (
      <span className={cn(slot, "text-muted-foreground")}>
        <i className={cn("fa-light text-xs", glyph.icon)} aria-hidden />
      </span>
    )
  }

  const filledClass = muted
    ? "bg-muted-foreground/30"
    : SCALE_TONE_CLASS[glyph.tone]

  return (
    <span className={cn(slot, "items-end gap-px")} aria-hidden>
      {SCALE_BAR_HEIGHTS.map((height, index) => (
        <span
          key={height}
          className={cn(
            "w-1 rounded-sm",
            height,
            index < glyph.level ? filledClass : "bg-muted"
          )}
        />
      ))}
    </span>
  )
}

/**
 * One band of a spread, drawn as a full-width row whose fill is that band's
 * share of the section.
 *
 * This replaced a stacked segment bar with a detached legend. In a 256px rail
 * the stack forced two reads — match the colour, then find the label — and
 * segments of a stack have no common baseline, so comparing two bands meant
 * comparing widths that start in different places. Rows share a left edge and a
 * track width, so "which band is bigger" is answered by length alone.
 *
 * The row is a button: every band names something the author can go and find,
 * and a band sitting at zero is the most actionable one on screen ("no Hard
 * yet" → go find Hard). The fill is `aria-hidden` because the row's text
 * already carries the label and the count.
 */
function MixBandRow({
  band,
  total,
  active,
  onSelect,
}: {
  band: MixBand
  total: number
  active: boolean
  onSelect: (band: MixBand) => void
}) {
  const fill = total > 0 ? (band.count / total) * 100 : 0

  return (
    <li>
      <button
        type="button"
        aria-pressed={active}
        onClick={() => onSelect(band)}
        className={cn(
          "relative flex min-h-7 w-full items-center gap-2 overflow-hidden rounded-1 px-1.5 text-start",
          "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
          "hover:bg-muted/50",
          active && "ring-1 ring-brand-1"
        )}
      >
        <span
          aria-hidden
          className={cn(
            "absolute inset-y-0 start-0 rounded-1 transition-[width]",
            band.fillClassName
          )}
          // Width, not flex-grow: each row is measured against the section
          // total so the tracks stay comparable between rows.
          style={{ width: `${fill}%` }}
        />
        <MixBandGlyph glyph={band.glyph} muted={band.count === 0} />
        <span
          className={cn(
            "relative min-w-0 flex-1 truncate text-sm",
            band.count > 0 ? "text-foreground" : "text-muted-foreground"
          )}
        >
          {band.label}
        </span>
        {/* Count only — the bar behind the row already draws the share, so the
            percentage was the same fact twice in a rail this narrow. */}
        <span className="relative shrink-0 text-sm text-muted-foreground tabular-nums">
          {band.count}
        </span>
      </button>
    </li>
  )
}

/**
 * A spread of the section — difficulty, Bloom's, topic, discrimination.
 *
 * `group.read` is usually `null` and that is the design: a sentence only
 * appears when it states something the bars cannot ("nothing Hard yet",
 * "3 not tagged"). A line under every heading meant four captions narrating
 * four charts, and the two that mattered were lost among the two that did not.
 */
function MixBandList({
  id,
  title,
  total,
  group,
  activeFacetScope,
  action,
  onSelect,
}: {
  id: string
  title: string
  total: number
  group: MixGroup
  activeFacetScope: string | null
  /**
   * Control that belongs to this spread and nothing else — difficulty's index
   * gauge, discrimination's method switch. In the heading row rather than above
   * the bars, so it cannot be mistaken for a band.
   */
  action?: React.ReactNode
  onSelect: (band: MixBand) => void
}) {
  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <h3 id={id} className="min-w-0 text-sm font-medium text-foreground">
          {title}
        </h3>
        {action}
      </div>
      {group.read ? (
        <p className="text-sm text-muted-foreground">{group.read}</p>
      ) : null}
      <ul aria-labelledby={id} className="mt-0.5 flex flex-col gap-0.5">
        {group.bands.map((band) => (
          <MixBandRow
            key={band.key}
            band={band}
            total={total}
            active={activeFacetScope === mixFacetScope(band.facet)}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </section>
  )
}

function MixSection({
  id,
  title,
  hint,
  children,
}: {
  id: string
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 id={id} className="text-sm font-medium text-foreground">
        {title}
      </h3>
      <ul aria-labelledby={id} className="flex flex-col gap-2.5">
        {children}
      </ul>
      {hint ? <p className="text-sm text-muted-foreground">{hint}</p> : null}
    </section>
  )
}

/* ------------------------------------------------------- blueprint coverage */

interface CoverageBloomNode {
  bloom: BloomLevel
  filled: number
  target: number
  entries: SectionGapProgress[]
}

interface CoverageTopicNode {
  categoryId: string
  topic: string
  filled: number
  target: number
  blooms: CoverageBloomNode[]
}

/**
 * Targets nested topic > Bloom's level > question type.
 *
 * One flat row per target crammed four facts into a 300px label — topic, level,
 * difficulty, type — and truncated the topic to make room, which is the one fact
 * the author navigates by. Nesting spends the width on one fact per level and
 * lets the shared parents carry a total, so "Cardiology is 2 of 6" is legible
 * without adding up its children.
 *
 * The order is the order the blueprint is argued in: a topic is what the paper
 * is about, the level is how deeply it is being tested, and the type is only the
 * instrument. Bloom's sits in the middle because that is the pairing an
 * accreditation review reads.
 */
function groupCoverage(gapProgress: SectionGapProgress[]): CoverageTopicNode[] {
  const topics: CoverageTopicNode[] = []

  for (const entry of gapProgress) {
    const filled = Math.min(entry.filled, entry.gap.targetCount)
    let topic = topics.find((node) => node.categoryId === entry.gap.categoryId)
    if (!topic) {
      topic = {
        categoryId: entry.gap.categoryId,
        topic: labelForContentId(entry.gap.categoryId),
        filled: 0,
        target: 0,
        blooms: [],
      }
      topics.push(topic)
    }
    topic.filled += filled
    topic.target += entry.gap.targetCount

    let bloom = topic.blooms.find((node) => node.bloom === entry.gap.bloom)
    if (!bloom) {
      bloom = { bloom: entry.gap.bloom, filled: 0, target: 0, entries: [] }
      topic.blooms.push(bloom)
    }
    bloom.filled += filled
    bloom.target += entry.gap.targetCount
    bloom.entries.push(entry)
  }

  // Ladder order, not first-seen: a plan reads Remember to Create.
  for (const topic of topics) {
    topic.blooms.sort(
      (a, b) => BLOOM_LEVELS.indexOf(a.bloom) - BLOOM_LEVELS.indexOf(b.bloom)
    )
  }

  return topics
}

/** Shared by the topic header and the bar under a target. */
function CoverageMeter({
  filled,
  target,
  labelledBy,
  satisfied,
}: {
  filled: number
  target: number
  labelledBy: string
  satisfied: boolean
}) {
  const percent = target > 0 ? Math.min(100, (filled / target) * 100) : 0
  return (
    <div
      role="progressbar"
      aria-labelledby={labelledBy}
      aria-valuemin={0}
      aria-valuemax={target}
      aria-valuenow={filled}
      className="h-1.5 overflow-hidden rounded-full bg-muted"
    >
      <div
        className={cn(
          "h-full rounded-full",
          satisfied ? "bg-brand-1" : "bg-brand-1/62"
        )}
        style={{ width: `${percent}%` }}
      />
    </div>
  )
}

function CoverageCount({
  filled,
  target,
  satisfied,
}: {
  filled: number
  target: number
  satisfied: boolean
}) {
  return (
    <span className="shrink-0 text-sm text-muted-foreground tabular-nums">
      {filled} of {target}
      {satisfied ? " · met" : null}
    </span>
  )
}

function CoverageTopicRow({ node }: { node: CoverageTopicNode }) {
  const satisfied = node.filled >= node.target
  const labelId = `mix-coverage-${slugify(node.topic)}`

  return (
    <li className="flex flex-col gap-1.5">
      {/* Open unless the topic is done. An author opens this panel to find what
          is missing, and a met topic is the one thing they do not need to read. */}
      <Collapsible defaultOpen={!satisfied} className="group/coverage">
        <CollapsibleTrigger
          className={cn(
            "flex w-full min-h-7 items-center gap-1.5 rounded-1 px-1 text-start",
            "hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          )}
        >
          <i
            className="fa-light fa-chevron-right w-3 shrink-0 text-xs text-muted-foreground transition-transform duration-150 group-data-[state=open]/coverage:rotate-90"
            aria-hidden
          />
          <span
            id={labelId}
            className="min-w-0 flex-1 truncate text-sm text-foreground"
          >
            {node.topic}
          </span>
          <CoverageCount
            filled={node.filled}
            target={node.target}
            satisfied={satisfied}
          />
        </CollapsibleTrigger>
        <div className="px-1">
          <CoverageMeter
            filled={node.filled}
            target={node.target}
            labelledBy={labelId}
            satisfied={satisfied}
          />
        </div>
        <OutlineTreeCollapsibleContentRail>
          <ul className="mt-1.5 flex flex-col gap-2 ps-4">
            {node.blooms.map((bloom) => (
              <CoverageBloomRow
                key={bloom.bloom}
                topic={node.topic}
                node={bloom}
              />
            ))}
          </ul>
        </OutlineTreeCollapsibleContentRail>
      </Collapsible>
    </li>
  )
}

function CoverageBloomRow({
  topic,
  node,
}: {
  topic: string
  node: CoverageBloomNode
}) {
  const satisfied = node.filled >= node.target

  return (
    <li className="flex flex-col gap-1">
      {/* No meter on this level: it is the sum of the rows under it, which each
          draw their own, and three stacked bars in a 300px rail read as one
          striped block. The icon names the level; the label beside it carries
          the meaning (§8.6 Case A). */}
      <div className="flex min-w-0 items-center gap-1.5">
        <i
          className={`fa-light ${BLOOM_ICON[node.bloom]} w-3.5 shrink-0 text-xs text-muted-foreground`}
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-sm text-foreground">
          {node.bloom}
        </span>
        <CoverageCount
          filled={node.filled}
          target={node.target}
          satisfied={satisfied}
        />
      </div>
      <ul className="flex flex-col gap-1.5 ps-5">
        {node.entries.map((entry) => (
          <MixTargetRow
            key={entry.scopeValue}
            labelId={`mix-${slugify(entry.scopeValue)}`}
            label={`${entry.gap.difficulty} ${entry.gap.questionType}`}
            count={Math.min(entry.filled, entry.gap.targetCount)}
            max={entry.gap.targetCount}
            satisfied={entry.satisfied}
          />
        ))}
      </ul>
      <span className="sr-only">
        {topic}, {node.bloom}
      </span>
    </li>
  )
}

function MixCoverage({ gapProgress }: { gapProgress: SectionGapProgress[] }) {
  const topics = React.useMemo(() => groupCoverage(gapProgress), [gapProgress])
  const id = "mix-blueprint"

  return (
    <section className="flex flex-col gap-2">
      <h3 id={id} className="text-sm font-medium text-foreground">
        Blueprint coverage
      </h3>
      <ul aria-labelledby={id} className="flex flex-col gap-2.5">
        {topics.map((topic) => (
          <CoverageTopicRow key={topic.topic} node={topic} />
        ))}
      </ul>
    </section>
  )
}

/* --------------------------------------------------------- difficulty index */

/**
 * How hard the section came out, as one ten-cell readout.
 *
 * A composite of the difficulty bands under it, which answer "how many Hard"
 * but not "is this paper hard" — an author comparing three sections needs the
 * second question answered without adding up three spreads. Segments rather than
 * a continuous meter because the number is a rounded mean of three ratings and a
 * smooth bar would claim a precision it does not have.
 *
 * `role="img"` with the name and the arithmetic, plus a tooltip, because the
 * cells are the only carrier of the value on screen (§8.6 Case B).
 */
function DifficultyIndexGauge({ index }: { index: number | null }) {
  if (index === null) return null
  const label = `Difficulty index ${index} of ${DIFFICULTY_INDEX_CELLS}, ${difficultyIndexLabel(index)}`

  return (
    <Tip
      label={`${label}. The average of this section's ratings, where Easy is 1, Medium is 5, and Hard is 10.`}
      side="left"
    >
      <span
        role="img"
        aria-label={label}
        tabIndex={0}
        className="flex min-h-6 shrink-0 items-center gap-1.5 rounded-1 px-1 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        <span aria-hidden className="flex items-end gap-px">
          {Array.from({ length: DIFFICULTY_INDEX_CELLS }, (_, cell) => {
            const position = cell + 1
            return (
              <span
                key={position}
                className={cn(
                  "h-3 w-1 rounded-sm",
                  position <= index
                    ? SCALE_TONE_CLASS[difficultyIndexCellTone(position)]
                    : "bg-muted"
                )}
              />
            )
          })}
        </span>
        <span
          aria-hidden
          className="text-sm text-muted-foreground tabular-nums"
        >
          {index}/{DIFFICULTY_INDEX_CELLS}
        </span>
      </span>
    </Tip>
  )
}

/* ------------------------------------------------- discrimination method set */

/**
 * Which arithmetic the discrimination spread uses. Two buttons rather than a
 * `Select`: there are exactly two, both fit, and a closed select would hide the
 * fact that the other reading exists.
 */
function DiscriminationMethodSwitch({
  value,
  onChange,
}: {
  value: DiscriminationMethodId
  onChange: (id: DiscriminationMethodId) => void
}) {
  return (
    <div
      role="group"
      aria-label="Discrimination method"
      className="flex shrink-0 items-center gap-0.5 rounded-1 border border-border p-0.5"
    >
      {DISCRIMINATION_METHODS.map((method) => (
        <Tip key={method.id} label={`${method.label}. ${method.explainer}`}>
          <button
            type="button"
            // The abbreviation leads so the visible text is contained in the
            // accessible name (SC 2.5.3), and the full name follows because
            // "D" on its own tells a screen reader nothing.
            aria-label={`${method.abbreviation}, ${method.label}`}
            aria-pressed={value === method.id}
            onClick={() => onChange(method.id)}
            className={cn(
              "flex min-h-6 items-center gap-1 rounded-1 px-1.5 text-xs",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
              value === method.id
                ? "bg-brand-1/12 font-medium text-foreground"
                : "text-muted-foreground hover:bg-muted/50"
            )}
          >
            <i className={`fa-light ${method.icon}`} aria-hidden />
            {method.abbreviation}
          </button>
        </Tip>
      ))}
    </div>
  )
}

/**
 * One section of the paper, folded shut.
 *
 * The header carries what a comparison needs — how many questions, how many
 * points, and how hard it came out — so the three sections can be read against
 * each other without opening any of them. The spreads live inside because they
 * are a second question ("hard how?"), and three sections' worth of them
 * unfolded is a rail nobody scrolls to the end of.
 *
 * The section being added to opens by default: it is the one the author came
 * here for, and making them click to see it would be a step with no decision in
 * it (P13).
 */
function MixSectionDisclosure({
  section,
  activeFacetScope,
  method,
  onMethodChange,
  onSelect,
}: {
  section: MixSectionRoster
  activeFacetScope: string | null
  method: DiscriminationMethodId
  onMethodChange: (id: DiscriminationMethodId) => void
  onSelect: (band: MixBand) => void
}) {
  const total = section.rows.length
  const points = section.rows.reduce((sum, row) => sum + (row.points ?? 1), 0)
  const prefix = `mix-section-${slugify(section.id)}`

  const { bloom, topics, discrimination } = React.useMemo(
    () => ({
      bloom: buildBloomMix(section.rows),
      topics: buildTopicMix(section.rows),
      discrimination: buildDiscriminationMix(section.rows, method),
    }),
    [method, section.rows]
  )

  return (
    <li>
      <Collapsible
        defaultOpen={section.current}
        className="group/section rounded-2 border border-border"
      >
        <CollapsibleTrigger
          className={cn(
            "flex w-full flex-col gap-1 rounded-2 px-2 py-1.5 text-start",
            "hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          )}
        >
          <span className="flex w-full min-w-0 items-center gap-1.5">
            <i
              className="fa-light fa-chevron-right w-3 shrink-0 text-xs text-muted-foreground transition-transform duration-150 group-data-[state=open]/section:rotate-90"
              aria-hidden
            />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
              {section.id}. {section.title}
            </span>
            {section.current ? (
              <span className="shrink-0 rounded-1 bg-brand-1/12 px-1.5 text-xs text-foreground">
                Adding
              </span>
            ) : null}
          </span>
          <span className="flex w-full min-w-0 items-center gap-2 ps-[1.125rem]">
            <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground tabular-nums">
              {total} {total === 1 ? "question" : "questions"} · {points}{" "}
              {points === 1 ? "mark" : "marks"}
            </span>
          </span>
        </CollapsibleTrigger>
        {/* Outside the trigger: the gauge is focusable in its own right (it
            carries the tooltip that explains the arithmetic), and a focus stop
            inside a button is a control inside a control. */}
        <div className="flex justify-end px-2 pb-1.5">
          <DifficultyIndexGauge index={sectionDifficultyIndex(section.rows)} />
        </div>
        <OutlineTreeCollapsibleContentRail>
          {total === 0 ? (
            <p className="px-2 pb-2 text-sm text-muted-foreground">
              Nothing in this section yet.
            </p>
          ) : (
            <div className="flex flex-col gap-4 px-2 pt-1 pb-2.5">
              <MixBandList
                id={`${prefix}-bloom`}
                title="Bloom’s taxonomy"
                total={total}
                group={bloom}
                activeFacetScope={activeFacetScope}
                onSelect={onSelect}
              />
              <MixBandList
                id={`${prefix}-topic`}
                title="Topics"
                total={total}
                group={topics}
                activeFacetScope={activeFacetScope}
                onSelect={onSelect}
              />
              <MixBandList
                id={`${prefix}-discrimination`}
                title="Discrimination"
                total={total}
                group={discrimination}
                activeFacetScope={activeFacetScope}
                action={
                  <DiscriminationMethodSwitch
                    value={method}
                    onChange={onMethodChange}
                  />
                }
                onSelect={onSelect}
              />
            </div>
          )}
        </OutlineTreeCollapsibleContentRail>
      </Collapsible>
    </li>
  )
}

function MixTab({
  rows,
  sections,
  gapProgress,
  hasBlueprint,
  onOpenBlueprint,
  activeScope,
  defaultScope,
  onScopeChange,
  method,
  onMethodChange,
}: {
  rows: AssessmentBankQuestion[]
  sections: MixSectionRoster[]
  gapProgress: SectionGapProgress[]
  hasBlueprint: boolean
  onOpenBlueprint: () => void
  activeScope: string
  /** Where a cleared band returns the pool to. */
  defaultScope: string
  onScopeChange: (scope: string) => void
  /**
   * Which arithmetic every discrimination spread uses. One setting for the whole
   * flow, not one per section: an author comparing A with B has to be comparing
   * them on the same measure or the comparison is meaningless, and the question
   * preview reports the same measure this tab is set to.
   */
  method: DiscriminationMethodId
  onMethodChange: (method: DiscriminationMethodId) => void
}) {
  const total = rows.length
  const activeFacet = parseMixFacetScope(activeScope)
  const activeFacetScope = activeFacet ? activeScope : null
  const setMethod = onMethodChange

  const difficulty = React.useMemo(() => buildDifficultyMix(rows), [rows])
  const points = React.useMemo(
    () => rows.reduce((sum, row) => sum + (row.points || 0), 0),
    [rows]
  )
  const difficultyIndex = React.useMemo(
    () => sectionDifficultyIndex(rows),
    [rows]
  )
  const gapsFilled = React.useMemo(() => {
    if (gapProgress.length === 0) return null
    const filled = gapProgress.filter((gap) => gap.satisfied).length
    return { filled, total: gapProgress.length }
  }, [gapProgress])

  const mixMetrics = React.useMemo((): MetricItem[] => {
    const difficultySeries = difficulty.bands.map((band) => band.count)
    const metrics: MetricItem[] = [
      {
        id: "mix-questions",
        label: "In section",
        value: total,
        delta: "",
        trend: "neutral",
        description: total === 1 ? "question" : "questions",
        chart: {
          kind: "sparkline",
          series: difficultySeries.length > 0 ? difficultySeries : [total],
          tone: "brand",
        },
      },
      {
        id: "mix-points",
        label: "Marks",
        value: points,
        delta: "",
        trend: "neutral",
        description: "across the section",
        chart: {
          kind: "bars",
          series: difficultySeries.length > 0 ? difficultySeries : [points],
          tone: "brand",
        },
      },
    ]
    if (difficultyIndex !== null) {
      metrics.push({
        id: "mix-difficulty",
        label: "Difficulty",
        value: `${difficultyIndex}/${DIFFICULTY_INDEX_CELLS}`,
        delta: "",
        trend: "neutral",
        description: difficultyIndexLabel(difficultyIndex),
        trendPolarity: "informational",
        chart: {
          kind: "bars",
          series: difficultySeries,
          tone: "info",
        },
      })
    }
    if (gapsFilled) {
      metrics.push({
        id: "mix-blueprint",
        label: "Blueprint",
        value: `${gapsFilled.filled}/${gapsFilled.total}`,
        delta: "",
        trend: "neutral",
        description: "gaps filled",
        progress: gapsFilled.filled,
        progressMax: gapsFilled.total,
        alert:
          gapsFilled.filled < gapsFilled.total ? "warning" : undefined,
      })
    }
    return metrics.slice(0, 4)
  }, [difficulty.bands, difficultyIndex, gapsFilled, points, total])

  /** Clicking the band the pool already shows is the way back out of it. */
  const selectBand = React.useCallback(
    (band: MixBand) => {
      const scope = mixFacetScope(band.facet)
      onScopeChange(scope === activeScope ? defaultScope : scope)
    },
    [activeScope, defaultScope, onScopeChange]
  )

  // Nothing to measure against: without a blueprint the coverage rows would be
  // an empty section, and the mix below them has no target to be short of.
  // Offer the plan instead — copied from the data-poor addition flow.
  if (!hasBlueprint && total === 0) {
    return (
      <BankEmptyState
        density="panel"
        icon="fa-list-check"
        title="No blueprint for this assessment"
        description="Adding questions is faster with a plan. Set the topics and counts you need, and the mix here measures against them."
        actions={[
          {
            label: "Set up a blueprint",
            icon: "fa-list-check",
            onClick: onOpenBlueprint,
          },
        ]}
      />
    )
  }

  if (total === 0) {
    return (
      <div className="flex flex-col gap-6">
        <p className="text-sm text-muted-foreground">
          This is what the section still needs.
        </p>
        <MixCoverage gapProgress={gapProgress} />
        {/* No buttons: adding happens in the pool beside this rail, and the
            header already carries Import and Compose new. Repeating them here
            would be a third copy of the same two verbs on one screen. */}
        <BankEmptyState
          density="panel"
          icon="fa-chart-simple"
          title="Nothing to measure yet"
          description="Difficulty, Bloom&rsquo;s, topic, and discrimination fill in as you add questions."
        />
        <MixSectionList
          sections={sections}
          activeFacetScope={activeFacetScope}
          method={method}
          onMethodChange={setMethod}
          onSelect={selectBand}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {total > 0 ? (
        <KeyMetrics
          variant="cards"
          size="sm"
          metrics={mixMetrics}
          showHeader={false}
          metricsSingleRow
        />
      ) : null}
      <div className="flex flex-col gap-1.5">
        <p className="text-sm text-muted-foreground">
          Spread across the{" "}
          <span className="font-medium text-foreground tabular-nums">
            {total}
          </span>{" "}
          {total === 1 ? "question" : "questions"} in this section.
        </p>
        {activeFacet ? (
          // The pool is showing a slice the author picked from this panel, so
          // the way back sits next to the thing that took them there.
          <div className="flex items-center gap-2 rounded-2 border border-border px-2 py-1.5">
            <span className="min-w-0 flex-1 truncate text-sm text-foreground">
              Pool: {mixFacetLabel(activeFacet)}
            </span>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="-me-1 h-6 shrink-0 px-2"
              onClick={() => onScopeChange(defaultScope)}
            >
              Clear
            </Button>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Select a band to find matching questions.
          </p>
        )}
      </div>
      {hasBlueprint ? (
        <MixCoverage gapProgress={gapProgress} />
      ) : (
        // The spreads below are still worth reading without a blueprint; only
        // the "am I on target" half is missing, so this replaces that section
        // rather than taking over the tab.
        <section className="flex flex-col items-start gap-2 rounded-2 border border-border p-3">
          <h3 className="text-sm font-medium text-foreground">
            No blueprint to measure against
          </h3>
          <p className="text-sm text-muted-foreground">
            Set the topics and counts this assessment needs and coverage shows
            up here.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onOpenBlueprint}
          >
            <i className="fa-light fa-list-check" aria-hidden />
            Set up a blueprint
          </Button>
        </section>
      )}

      {/* Difficulty stays out here, at the top of the spreads and above the
          per-section fold: it is the question authors ask of a section first,
          and the gauge in each section header is a summary of exactly this. */}
      <MixBandList
        id="mix-difficulty"
        title="Difficulty"
        total={total}
        group={difficulty}
        activeFacetScope={activeFacetScope}
        action={<DifficultyIndexGauge index={sectionDifficultyIndex(rows)} />}
        onSelect={selectBand}
      />

      <MixSectionList
        sections={sections}
        activeFacetScope={activeFacetScope}
        method={method}
        onMethodChange={setMethod}
        onSelect={selectBand}
      />
    </div>
  )
}

function MixSectionList({
  sections,
  activeFacetScope,
  method,
  onMethodChange,
  onSelect,
}: {
  sections: MixSectionRoster[]
  activeFacetScope: string | null
  method: DiscriminationMethodId
  onMethodChange: (id: DiscriminationMethodId) => void
  onSelect: (band: MixBand) => void
}) {
  if (sections.length === 0) return null
  const id = "mix-sections"

  return (
    <section className="flex flex-col gap-2">
      <h3 id={id} className="text-sm font-medium text-foreground">
        Sections
      </h3>
      <ul aria-labelledby={id} className="flex flex-col gap-1.5">
        {sections.map((section) => (
          <MixSectionDisclosure
            key={section.id}
            section={section}
            activeFacetScope={activeFacetScope}
            method={method}
            onMethodChange={onMethodChange}
            onSelect={onSelect}
          />
        ))}
      </ul>
    </section>
  )
}

/**
 * Leo's star, boxed to the same slot a Font Awesome nav glyph occupies. The
 * star's own span is 20px, so without the fixed slot the label beside it sits a
 * few pixels right of every other row.
 *
 * `leading-[0]` matters: LeoIcon wraps its inline-flex star in a block, so the
 * inherited line-height leaves descender space under it and the glyph rides
 * ~2px above the label's cap band. Zeroing the strut re-centres it.
 *
 * The ambient icon stays in its default rest state here: scope identity should
 * remain stable while the results pane does the changing.
 */
export function LeoScopeIcon() {
  return (
    <span
      aria-hidden
      className="flex h-4 w-[1.125rem] shrink-0 items-center justify-center leading-[0]"
    >
      <LeoIcon size="xs" />
    </span>
  )
}

function BankNavRow({
  item,
  activeScope,
  onScopeChange,
  depth = 0,
}: {
  item: BankNavItem
  activeScope: string
  onScopeChange: (scope: string) => void
  depth?: number
}) {
  const isActive = activeScope === item.value
  const children = item.children ?? []

  const row = (
    <OutlineTreeLeafButton
      surface="panel"
      isActive={isActive}
      aria-current={isActive ? "true" : undefined}
      aria-label={item.isNew ? `${item.label}, new` : undefined}
      onClick={() => onScopeChange(item.value)}
      className={cn(
        "w-full justify-between gap-2",
        item.isNew &&
          !isActive &&
          "bg-dt-new-row-bg text-sidebar-foreground hover:bg-dt-new-row-bg/80"
      )}
    >
      <span className="flex min-w-0 items-center gap-2">
        {item.leo ? (
          <LeoScopeIcon />
        ) : (
          <i className={`fa-light ${item.icon}`} aria-hidden />
        )}
        <span className="truncate">{item.label}</span>
      </span>
      {item.busy ? (
        <span className="flex shrink-0 items-center gap-1.5">
          {item.isNew ? <TableNewRowDot label={null} /> : null}
          <i
            className="fa-light fa-spinner-third animate-spin text-sm text-muted-foreground"
            role="img"
            aria-label="Still importing"
          />
        </span>
      ) : (item.meta ?? item.count?.toLocaleString()) ? (
        <span className="flex shrink-0 items-center gap-1.5 text-sm text-muted-foreground tabular-nums">
          {item.isNew ? <TableNewRowDot label={null} /> : null}
          {item.meta ?? item.count?.toLocaleString()}
        </span>
      ) : item.isNew ? (
        <TableNewRowDot label={null} />
      ) : null}
    </OutlineTreeLeafButton>
  )

  if (children.length === 0) {
    return (
      <OutlineTreeMenuItem style={outlineTreeBranchDepthStyle(depth)}>
        {row}
      </OutlineTreeMenuItem>
    )
  }

  return (
    <OutlineTreeMenuItem style={outlineTreeBranchDepthStyle(depth)}>
      <Collapsible defaultOpen={false} className="group/collapsible">
        <div className="flex min-w-0 items-center">
          <Tip label={`Expand ${item.label}`}>
            <CollapsibleTrigger asChild>
              <OutlineTreeExpandTrigger
                // Branches are not only folders here (Past assessments has one
                // child per assessment), and `aria-expanded` from the trigger
                // already carries the state, so the name states the action once.
                aria-label={`Expand ${item.label}`}
              >
                <i
                  className="fa-light fa-chevron-right text-xs transition-transform duration-150 group-data-[state=open]/collapsible:rotate-90"
                  aria-hidden
                />
              </OutlineTreeExpandTrigger>
            </CollapsibleTrigger>
          </Tip>
          <span className="min-w-0 flex-1">{row}</span>
        </div>
        <OutlineTreeCollapsibleContentRail>
          <OutlineTreeSub surface="panel" guideLayout="chevronRail">
            {children.map((child) => (
              <BankNavRow
                key={child.value}
                item={child}
                activeScope={activeScope}
                onScopeChange={onScopeChange}
                depth={depth + 1}
              />
            ))}
          </OutlineTreeSub>
        </OutlineTreeCollapsibleContentRail>
      </Collapsible>
    </OutlineTreeMenuItem>
  )
}

/**
 * One heading and the rows under it, with the tail folded away when there are
 * more of them than the rail can spend its height on.
 *
 * The cap is a count of rows rather than a scroll height because the rows are
 * the unit the author reads: "the five most recent papers, and there are more"
 * is a sentence, where "as many as fit" is an accident of the viewport.
 */
function BankNavSection({
  group,
  activeScope,
  onScopeChange,
}: {
  group: BankNavGroup
  activeScope: string
  onScopeChange: (scope: string) => void
}) {
  const [showAll, setShowAll] = React.useState(false)

  const cap = group.collapseAfter ?? group.items.length
  const hidden = group.items.slice(cap)
  /**
   * A scope that is open cannot be one of the hidden rows: the rail would be
   * pointing at nothing while the pool showed that paper's questions. Reaching
   * one by deep link or by picking it in the Location tree therefore opens the
   * group, and it stays open.
   */
  const holdsActive = hidden.some((item) => item.value === activeScope)
  const expanded = showAll || holdsActive
  const visible = expanded ? group.items : group.items.slice(0, cap)

  const headingId = `bank-nav-${group.id}`
  const listId = `bank-nav-list-${group.id}`

  return (
    <section className="flex flex-col gap-1">
      {group.hideLabel ? null : (
        <h3
          id={headingId}
          className="px-2 text-sm font-medium text-muted-foreground"
        >
          {group.label}
        </h3>
      )}
      <OutlineTreeMenu
        id={listId}
        aria-labelledby={group.hideLabel ? undefined : headingId}
        aria-label={group.hideLabel ? group.label : undefined}
      >
        {visible.map((item) => (
          <BankNavRow
            key={item.value}
            item={item}
            activeScope={activeScope}
            onScopeChange={onScopeChange}
          />
        ))}
      </OutlineTreeMenu>
      {/* The rows' own metrics, so the toggle reads as the last row of the
          list rather than as a control sitting under it. */}
      {hidden.length > 0 && !holdsActive ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setShowAll((current) => !current)}
          className="h-8 justify-start gap-2 px-2 font-normal text-muted-foreground"
        >
          <i
            className={cn(
              "fa-light",
              expanded ? "fa-chevron-up" : "fa-chevron-down"
            )}
            aria-hidden
          />
          {/* How many more, not the total: the number that decides whether to
              open this is the size of what is still hidden. */}
          {expanded ? "Show fewer" : `Show ${hidden.length} more`}
        </Button>
      ) : null}
    </section>
  )
}

/** Bank scope tree — used in the rail and in the tree+details left column. */
export function FoldersTab({
  groups,
  activeScope,
  onScopeChange,
}: {
  groups: BankNavGroup[]
  activeScope: string
  onScopeChange: (scope: string) => void
}) {
  return (
    // A landmark, because these rows are how the author moves between the
    // section, the course, and the bank — not a list of filters.
    <nav aria-label="Bank scopes" className="flex flex-col gap-4">
      {groups.map((group) => (
        <BankNavSection
          key={group.id}
          group={group}
          activeScope={activeScope}
          onScopeChange={onScopeChange}
        />
      ))}
    </nav>
  )
}

export function BankSidePanel({
  sectionId,
  navGroups,
  activeScope,
  defaultScope,
  onScopeChange,
  addedRows,
  mixSections,
  totalPoints,
  gapProgress,
  hasBlueprint,
  onRemove,
  onPreview,
  onOpenBlueprint,
  assessmentGapProgress,
  assessmentRows,
  onFocusBlueprintTarget,
  onSelectBlueprintMixBand,
  activeMixFacetScope,
  discriminationMethod: method,
  onDiscriminationMethodChange,
}: {
  sectionId: string
  navGroups: BankNavGroup[]
  activeScope: string
  /** Where the Mix tab returns the pool to when a band is cleared. */
  defaultScope: string
  onScopeChange: (scope: string) => void
  addedRows: AssessmentBankQuestion[]
  /** Every section of the paper, so Mix can compare this one against the rest. */
  mixSections: MixSectionRoster[]
  totalPoints: number
  gapProgress: SectionGapProgress[]
  hasBlueprint: boolean
  onRemove: (rows: AssessmentBankQuestion[]) => void
  /** Opens the question preview sheet from the section Questions list. */
  onPreview?: (row: AssessmentBankQuestion) => void
  onOpenBlueprint: () => void
  /** Assessment-wide blueprint coverage (Blueprint tab). */
  assessmentGapProgress: BlueprintGapProgress[]
  assessmentRows: AssessmentBankQuestion[]
  onFocusBlueprintTarget: (
    entry: BlueprintGapProgress,
    intent: BlueprintTargetIntent
  ) => void
  onSelectBlueprintMixBand: (band: MixBand) => void
  activeMixFacetScope: string | null
  /**
   * Which discrimination measure Mix reports. Owned by the flow rather than by
   * this panel so the question preview names the same one.
   */
  discriminationMethod: DiscriminationMethodId
  onDiscriminationMethodChange: (method: DiscriminationMethodId) => void
}) {
  const [tab, setTab] = React.useState("folders")
  const total = addedRows.length
  const openBlueprintGaps = assessmentGapProgress.filter(
    (entry) => !entry.satisfied
  ).length
  const estimatedMinutes = sectionEstimatedMinutes(addedRows)
  const needsReviewCount = React.useMemo(
    () =>
      addedRows.filter((row) =>
        questionNeedsReview(questionSignalInputFromBankRow(row, method))
      ).length,
    [addedRows, method]
  )

  return (
    <Tabs
      value={tab}
      onValueChange={setTab}
      className="flex min-h-0 flex-1 flex-col gap-0"
    >
      <div className="min-w-0 shrink-0">
        <TabsList
          ariaLabel="Bank views"
          variant="line"
          className="w-fit justify-start"
        >
          <TabsTrigger value="folders" className="gap-1.5">
            <TabsTriggerIcon>
              <i className="fa-light fa-folders" aria-hidden />
            </TabsTriggerIcon>
            <TabsTriggerLabel>Bank</TabsTriggerLabel>
          </TabsTrigger>
          <TabsTrigger value="blueprint" className="gap-1.5">
            <TabsTriggerIcon>
              <i className="fa-light fa-bullseye-arrow" aria-hidden />
            </TabsTriggerIcon>
            <TabsTriggerLabel>Blueprint</TabsTriggerLabel>
            {openBlueprintGaps > 0 ? (
              <TabsCountBadge count={openBlueprintGaps} />
            ) : null}
          </TabsTrigger>
          {SHOW_MIX_TAB ? (
            <TabsTrigger value="mix" className="gap-1.5">
              <TabsTriggerIcon>
                <i className="fa-light fa-chart-pie" aria-hidden />
              </TabsTriggerIcon>
              <TabsTriggerLabel>Mix</TabsTriggerLabel>
            </TabsTrigger>
          ) : null}
          <TabsTrigger value="questions" className="gap-1.5">
            <TabsTriggerIcon>
              <i className="fa-light fa-list-ol" aria-hidden />
            </TabsTriggerIcon>
            <TabsTriggerLabel>Questions</TabsTriggerLabel>
            {total > 0 ? <TabsCountBadge count={total} /> : null}
          </TabsTrigger>
        </TabsList>
      </div>

      <div
        className={cn(
          "min-h-0 flex-1 overflow-y-auto overscroll-y-contain",
          PANEL_BODY_PADDING
        )}
      >
        <TabsContent value="folders">
          <FoldersTab
            groups={navGroups}
            activeScope={activeScope}
            onScopeChange={onScopeChange}
          />
        </TabsContent>

        <TabsContent value="blueprint" className="flex min-h-0 flex-1 flex-col">
          <BlueprintPanel
            gapProgress={assessmentGapProgress}
            rows={assessmentRows}
            discriminationMethodId={method}
            onDiscriminationMethodChange={onDiscriminationMethodChange}
            onFocusTarget={onFocusBlueprintTarget}
            onSelectMixBand={onSelectBlueprintMixBand}
            activeMixFacetScope={activeMixFacetScope}
          />
        </TabsContent>

        {SHOW_MIX_TAB ? (
          <TabsContent value="mix">
            <MixTab
              rows={addedRows}
              sections={mixSections}
              gapProgress={gapProgress}
              hasBlueprint={hasBlueprint}
              onOpenBlueprint={onOpenBlueprint}
              activeScope={activeScope}
              defaultScope={defaultScope}
              onScopeChange={onScopeChange}
              method={method}
              onMethodChange={onDiscriminationMethodChange}
            />
          </TabsContent>
        ) : null}

        <TabsContent value="questions" className="flex flex-col">
          {/* Rows, not cards — and no wrapper border: the panel already frames
              this list, so a second box just eats width. Rows run edge-to-edge
              (`-mx-2` against the scroller's padding) with hairline dividers,
              matching the Folders tree cadence beside it. */}
          <DataRowList
            rows={addedRows}
            getRowId={(row) => row.id}
            ariaLabel={`Questions added to Section ${sectionId}`}
            className={cn(QUESTION_ROW_LIST_CLASSNAME, PANEL_BODY_BLEED)}
            rowClassName={QUESTION_ROW_DIVIDER_CLASSNAME}
            emptyState={
              <BankEmptyState
                density="panel"
                icon="fa-list-ol"
                title="No questions yet"
                description="Add from the bank beside this panel and they line up here, in the order students will see them."
              />
            }
            renderRow={(row, index) => {
              const signalInput = questionSignalInputFromBankRow(row, method)
              const reviewReasons = questionReviewSignals(signalInput).map(
                (signal) => signal.label
              )
              return (
                <QuestionRow
                  position={index + 1}
                  label={row.title}
                  detail={row.stem}
                  // Difficulty reads as plain text here rather than as coloured
                  // signal bars: in a 300px rail the bars were the loudest thing
                  // on screen and they are not what the author scans for. The
                  // pool grid keeps the bars, where a column header frames them
                  // and neighbouring rows make the length comparable.
                  meta={[row.difficulty, row.topic]}
                  trailing={`${row.points ?? 1} ${(row.points ?? 1) === 1 ? "mark" : "marks"}`}
                  onOpen={onPreview ? () => onPreview(row) : undefined}
                  needsReview={questionNeedsReview(signalInput)}
                  reviewReasons={reviewReasons}
                  actionMode="overlay"
                  action={
                    <Tip label="Remove from this section">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove ${row.title} from this section`}
                        onClick={() => onRemove([row])}
                      >
                        <i className="fa-light fa-xmark" aria-hidden />
                      </Button>
                    </Tip>
                  }
                />
              )
            }}
          />

          {/* Pinned to the bottom of the panel rather than sitting above the
              list: the totals are what the author checks last, and keeping them
              out of the top preserves scroll height for the roster itself. */}
          {total > 0 ? (
            <SectionQuestionsTotals
              questionCount={total}
              totalPoints={totalPoints}
              estimatedMinutes={estimatedMinutes}
              needsReviewCount={needsReviewCount}
              className={cn(
                "sticky -bottom-3 mt-2 bg-[var(--secondary-panel-bg)]",
                PANEL_BODY_BLEED
              )}
            />
          ) : null}
        </TabsContent>
      </div>
    </Tabs>
  )
}
